import React, { useEffect, useState } from "react";
import Button from "../../shared/Button";
import ReactMarkdown from "react-markdown";
import Badge from "shared/Badge/Badge";
import { BlogCategory, BlogProperty } from "../../types/blogType";
import Modal from "../../shared/Modal";
import Status from "../../shared/Status";
import axios from "axios";
import { useHistory } from "react-router-dom";
import { useHandler } from "../../hooks/useHandler";
import { RentripsURL } from "utils/request";

type BlogProps = {
	data?: BlogProperty | null;
	type?: "new" | "edit";
};

const NewBlog = ({ data, type = "new" }: BlogProps) => {
	const history = useHistory();
	const [{ loading, error }, handler] = useHandler();
	const [isWrite, setIsWrite] = useState<boolean>(true);
	const [property, setProperty] = useState<BlogProperty>({
		_id: "",
		title: "",
		subtitle: "",
		image: "",
		content: "",
		category: "Travel",
		status: "new",
	});

	const [open, setOpen] = useState<boolean>(false);

	const handleDraft = async () => {
		handler("loading", true);
		setProperty({ ...property, status: "draft" });
		try {
			const { data } =
				property.status === "new"
					? await axios.post(
							RentripsURL + "/api/blog",
							{ ...property, status: "draft" },
							{
								withCredentials: true,
							}
					  )
					: await axios.patch(
							RentripsURL + `/api/blog/${property._id}`,
							{ ...property, status: "draft" },
							{
								withCredentials: true,
							}
					  );
			if (data.success) {
				setTimeout(() => {
					handler("loading", false);
					handler("success", "published!");
				}, 1000);
			}
		} catch (error: any) {
			handler("error", error.message);

			setTimeout(() => {
				handler("error", "");
			}, 2000);
			handler("loading", false);
		}
	};

	const handlePublish = async () => {
		handler("loading", true);
		try {
			const { data } =
				property.status === "new"
					? await axios.post(
							RentripsURL + "/api/blog",
							{ ...property, status: "published" },
							{
								withCredentials: true,
							}
					  )
					: await axios.patch(
							RentripsURL + `/api/blog/${property._id}`,
							{ ...property, status: "published" },
							{
								withCredentials: true,
							}
					  );
			if (data.success) {
				setTimeout(() => {
					handler("loading", false);
					handler("success", "published!");
					history.push("/blog");
				}, 1000);
			}
		} catch (error: any) {
			handler("error", error.message);

			setTimeout(() => {
				handler("error", "");
			}, 2000);
			handler("loading", false);
		}
	};

	useEffect(() => {
		if (data) setProperty(data);
	}, [data]);

	const renderHeader = () => {
		return (
			<header className="container rounded-xl">
				<div className="max-w-screen-md mx-auto space-y-5">
					<Badge href="##" name={property.category || "Travel"} />
					<h1
						className="text-neutral-900 font-semibold text-3xl md:text-4xl md:!leading-[120%] lg:text-4xl max-w-4xl "
						title="Quiet ingenuity: 120,000 lunches and counting"
					>
						{property.title || "Title"}{" "}
					</h1>
					<span className="block text-base text-neutral-500 md:text-lg dark:text-neutral-400 pb-1">
						{property.subtitle || "Subtitle"}
					</span>
				</div>
			</header>
		);
	};

	const renderWriting = () => {
		return (
			<div>
				<div className="my-6 text-sm w-full flex items-center">
					<label className="mr-3 font-bold text-white">Title</label>
					<input
						className="px-3 py-2 rounded-md w-full"
						onChange={(e) =>
							setProperty((p) => {
								return { ...p, title: e.target.value };
							})
						}
						value={property.title}
						placeholder="Title for the blog"
					></input>
				</div>
				<div className="my-6 text-sm w-full flex items-center">
					<label className="mr-3 font-bold text-white">Subtitle</label>
					<input
						className="px-3 py-2 rounded-md w-full"
						onChange={(e) =>
							setProperty((p) => {
								return { ...p, subtitle: e.target.value };
							})
						}
						value={property.subtitle}
						placeholder="Subtitle to enrich the blog"
					></input>
				</div>
				<div className="grid grid-cols-2 gap-x-6 my-6">
					<div className=" text-sm w-full flex items-center">
						<label className="mr-3 font-bold text-white w-fit">Photo</label>
						<input
							className="px-3 py-2 rounded-md w-full"
							onChange={(e) =>
								setProperty((p) => {
									return { ...p, image: e.target.value };
								})
							}
							value={property.image}
							placeholder="Link fotonya aja gan"
						></input>
					</div>
					<div className=" text-sm w-full flex items-center">
						<label className="mr-3 font-bold text-white w-fit">Category</label>
						<select
							className="px-3 py-2 text-sm rounded-md w-full"
							onChange={(e) =>
								setProperty((p) => {
									return { ...p, category: e.target.value as BlogCategory };
								})
							}
							value={property.category}
							placeholder="Link fotonya aja gan"
						>
							{["Travel", "Food", "Lifestyle", "Tips", "Others"].map(
								(category) => {
									return (
										<option key={category} value={category}>
											{category}
										</option>
									);
								}
							)}
						</select>
					</div>
				</div>
				<textarea
					onChange={(e) =>
						setProperty((p) => {
							return { ...p, content: e.target.value };
						})
					}
					value={property.content}
					className=" block w-full px-3 py-1.5 text-base font-normal text-gray-700 bg-white  border border-solid border-gray-300 rounded transition ease-in-out m-0 focus:text-gray-700 focus:bg-white focus:shadow-lg
    "
					id="blog-new-textarea"
					rows={20}
					placeholder="Start write your blog here"
				></textarea>
			</div>
		);
	};

	return (
		<div id="blog-new">
			<div className="flex items-center justify-between my-10">
				<div className="flex">
					{" "}
					<Button
						className={`bg-white rounded-r-none px-10 ${
							isWrite || "opacity-50"
						}`}
						onClick={() => setIsWrite(true)}
					>
						Write
					</Button>
					<Button
						className={`bg-white rounded-l-none px-10 ${
							isWrite && "opacity-50"
						}`}
						onClick={() => setIsWrite(false)}
					>
						View
					</Button>
				</div>

				<Status
					className=" bg-white px-3 py-2 rounded-lg"
					status={property.status}
				/>
			</div>
			<div id="blog-new-body">
				{isWrite ? (
					renderWriting()
				) : (
					<div className="block w-full text-base font-normal text-gray-700 bg-white  border border-solid border-gray-300 rounded transition ease-in-out m-0 focus:text-gray-700 focus:bg-white focus:shadow-lg py-10 px-10">
						{renderHeader()}
						<div className="h-96 relative overflow-hidden my-10 sm:my-12 rounded-xl">
							{" "}
							<img
								className="object-center object-cover h-full w-full hover:scale-125  hover:invert
                duration-1000"
								src={
									property.image ||
									"https://images.pexels.com/photos/1386603/pexels-photo-1386603.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=750&w=1260"
								}
								alt={property.title}
							/>
						</div>

						<ReactMarkdown
							children={property.content || `> Content here`}
							className="prose prose-sm !max-w-screen-md sm:prose mx-auto"
						></ReactMarkdown>
					</div>
				)}
				<div className="flex gap-x-3 my-6 w-full justify-end">
					<Button
						className="border-2 border-white w-fit !text-white"
						onClick={handleDraft}
					>
						Save as Draft
					</Button>
					<Button className="w-fit bg-white" onClick={() => setOpen(true)}>
						Publish
					</Button>
				</div>
			</div>
			<Modal
				title="Publish Blog?"
				handleOpen={() => setOpen(!open)}
				open={open}
			>
				{error && (
					<div className="text-slate-800 py-6 bg-red-200 px-3 rounded-md my-10">
						{error}
					</div>
				)}

				<div className="flex w-full justify-end">
					<Button
						className="text-red-400 border-0 focus:outline-0"
						onClick={() => setOpen(false)}
						autofocus={false}
					>
						Cancel
					</Button>
					<Button
						loading={loading}
						className="border border-sky-500"
						autofocus={true}
						onClick={handlePublish}
					>
						Yes
					</Button>
				</div>
			</Modal>
		</div>
	);
};

export default NewBlog;
